import { Assistant } from "@inrev/assistant";
import type { Relations } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { match } from "ts-pattern";

export const assistantEmptyCopy = (relation: Relations.Notes): string => {
	if (relation.relationType === "quote") return "Ask about this bond request";
	if (relation.relationType === "bond") return "Ask about this bond";
	return "Ask about this account";
};

export const assistantComposerPlaceholder = (relation: Relations.Notes): string =>
	assistantEmptyCopy(relation);

export const assistantSuggestedPrompts = (relation: Relations.Notes): string[] => {
	if (relation.relationType === "quote") {
		return [
			"Summarize this bond request",
			"What is the bond amount and type?",
			"Are there missing requirements?",
		];
	}
	if (relation.relationType === "bond") {
		return ["Summarize this bond", "What is the current term?", "Any related requests?"];
	}
	return [
		"What's the current exposure?",
		"Summarize recent underwriting",
		"Any open bond requests?",
	];
};

/**
 * Offered next questions, shown above the composer once a thread is underway. Deliberately
 * shorter than the empty-state prompts — they sit beside the composer, not instead of it.
 */
export const assistantFollowUpPrompts = (relation: Relations.Notes): string[] =>
	match(relation)
		.with({ relationType: "account" }, () => [
			"What's driving the WIP backlog?",
			"Explain the review reasons",
		])
		.with({ relationType: "quote" }, () => [
			"What could hold this up?",
			"How does this compare to the bonding line?",
		])
		.with({ relationType: "bond" }, () => [
			"What changed at the last renewal?",
			"Explain the review reasons",
		])
		.exhaustive();

/**
 * Labels for the tools still defined agent-side in Python. Every entry is debt: a ported tool
 * carries its own label in the catalog, so its entry here must go when it lands. The spec
 * enforces that — the catalog wins the lookup, so a stale entry would sit invisible behind it.
 *
 * Keyed by `LegacyPythonToolName`, so a name dropped from that registry fails to compile here
 * rather than leaving a label nothing can reach.
 */
export const LEGACY_PYTHON_TOOL_LABELS: Record<Assistant.Tools.LegacyPythonToolName, string> = {
	get_account_activity_summary: "Activity summary",
	get_account_summary: "Account summary",
	get_file_extraction: "Document extraction",
	get_knowledge: "Underwriting knowledge",
	get_financial_trends: "Financial trends",
	get_financials: "Financial statements",
	get_wip: "WIP schedule",
	list_events: "Account events",
	list_files: "Documents",
	list_messages: "Messages",
	ask_document: "Document read",
	ask_org_chart: "Org chart",
	run_character_check: "Character check",
	ask_web: "Web search",
	run_chart_code: "Custom chart",
	// The standing exception to the rule above: `query_file` was renamed, not ported, and
	// `assistant_tool_call` rows persist `toolName` verbatim and are retained indefinitely.
	// Dropping it would degrade every transcript written before the rename. No live tool
	// emits it.
	query_file: "Document read",
};

/**
 * Built once rather than searched per row; a rendered answer looks up every source it lists.
 * Proxy-audience tools are included: which calls are worth showing is the panel's decision.
 */
const CATALOG_TOOL_LABELS: Record<string, string> = Object.fromEntries(
	Assistant.Tools.TOOL_DEFINITIONS.map((definition) => [definition.name, definition.label]),
);

/** Widened for lookup: the labels are declared per known name, but callers arrive with a string. */
const LEGACY_TOOL_LABELS: Record<string, string> = LEGACY_PYTHON_TOOL_LABELS;

/** Catalog, then the Python holdouts, then the un-snaked name so an unknown tool reads as words. */
export const assistantToolLabel = (toolName: string): string =>
	CATALOG_TOOL_LABELS[toolName] ?? LEGACY_TOOL_LABELS[toolName] ?? toolName.split("_").join(" ");

export const assistantSourcesSummary = (count: number): string => `Sources & tool calls · ${count}`;

type ToolCallDetail = {
	isError: boolean;
	status?: Option<string>;
	resultRowCount?: Option<number>;
	durationMs?: Option<number>;
};

/**
 * The trailing detail on a tool-call row: what it returned, or how long it took when the count
 * is unknown. A failed call says so rather than reporting an empty result as a real one.
 *
 * `isError` is read before `status` because only it means the tool could not run; a status of
 * `invalid_input`, `not_found`, or `unavailable` is a real answer, and all three would otherwise
 * read as "0 rows".
 */
export const assistantToolDetail = (toolCall: ToolCallDetail): string | undefined => {
	if (toolCall.isError) return "unavailable";
	if (toolCall.status === "invalid_input") return "invalid arguments";
	if (toolCall.status === "not_found") return "not found";
	if (toolCall.status === "unavailable") return "unavailable";
	const rows = toolCall.resultRowCount;
	if (Option.isSome(rows)) return rows === 1 ? "1 row" : `${rows} rows`;
	const duration = toolCall.durationMs;
	if (Option.isSome(duration))
		return duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(1)}s`;
	return undefined;
};

export const ASSISTANT_CLEAR_COMMAND = "/clear";

export const assistantNewChatLabel = "New conversation";

/**
 * When a conversation was last active, in the reader's zone: the time today, then "Yesterday", then
 * the date, adding the year once it isn't this one.
 */
export const assistantConversationTime = (iso: string, now: DateTime = DateTime.now()): string => {
	const at = DateTime.fromISO(iso).setZone(now.zone);
	if (at.hasSame(now, "day")) return at.toFormat("h:mm a");
	if (at.hasSame(now.minus({ days: 1 }), "day")) return "Yesterday";
	if (at.hasSame(now, "year")) return at.toFormat("MMM d");
	return at.toFormat("MMM d, yyyy");
};

export const assistantHistoryLabel = "History";
export const assistantHistoryHeading = "Your conversations on this account";
export const assistantHistoryEmptyTitle = "No conversations yet";
export const assistantAskQuestionLabel = "Ask a question";
export const assistantHistoryEmptyCopy = (accountName: string): string =>
	`Questions you ask Verni about ${accountName} will show up here so you can come back to them.`;

export const isAssistantClearCommand = (message: string): boolean =>
	message.trim().toLowerCase() === ASSISTANT_CLEAR_COMMAND;

export type AssistantCommand = {
	command: string;
	description: string;
};

/**
 * What the `/` menu offers, in this order. `/clear` is handled here in the portal; every other
 * command is a skill run by uw-agent (`apps/uw-agent/skills.py`), so a new skill is added in both
 * places.
 */
export const ASSISTANT_COMMANDS: readonly AssistantCommand[] = [
	{ command: "/character", description: "Research account parties on the web" },
	{ command: ASSISTANT_CLEAR_COMMAND, description: "Start a new chat" },
	{
		command: "/orgchart",
		description: "Map the org chart from the latest FYE statements, against the prior year",
	},
];

/** Whether a draft is still only a command being typed: a slash and no whitespace yet. */
export const isAssistantCommandDraft = (draft: string): boolean =>
	draft.startsWith("/") && !/\s/.test(draft);

/** The commands whose name, without its slash, contains what follows the slash. */
export const matchingAssistantCommands = (draft: string): readonly AssistantCommand[] => {
	if (!isAssistantCommandDraft(draft)) return [];
	const typed = draft.slice(1).toLowerCase();
	return ASSISTANT_COMMANDS.filter(({ command }) => command.slice(1).includes(typed));
};

export const assistantCopyAnswerLabel = "Copy answer";
export const assistantCopiedLabel = "Copied";

export const assistantCopyChartImageLabel = "Copy chart image";
export const assistantSaveChartImageLabel = "Save chart image";
export const assistantCopyChartImageFailed = "Could not copy the chart image";
export const assistantMakeChartImageFailed = "Could not make the chart image";

export const assistantInterruptedLabel = "Answer was interrupted";
export const assistantTryAgainLabel = "Try again";

export type AssistantToolCall = Pick<
	Assistant.Dtos.Conversation.ToolCall,
	"toolName" | "isError"
> & {
	status?: Option<string>;
	resultRowCount?: Option<number>;
	durationMs?: Option<number>;
	/** True between a streamed `tool_call` and its `tool_result`. */
	pending?: boolean;
};

/** What the slow tools are doing, shown while the agent waits on them. Keyed by tool name, so a
 * renamed tool fails to compile here rather than losing its copy. */
const TOOL_PROGRESS_LABELS: Partial<Record<Assistant.Tools.LegacyPythonToolName, string>> = {
	run_character_check: "Researching the account's parties on the public web…",
	ask_web: "Searching the web…",
};

/** Widened for lookup, as `LEGACY_TOOL_LABELS` is. */
const TOOL_PROGRESS_LOOKUP: Record<string, string> = TOOL_PROGRESS_LABELS;

/** The most recent slow tool still running, as progress copy; undefined while none is. */
export const assistantToolProgress = (toolCalls: readonly AssistantToolCall[]): Option<string> =>
	toolCalls
		.filter((toolCall) => toolCall.pending === true)
		.map(({ toolName }) => TOOL_PROGRESS_LOOKUP[toolName])
		.filter(Option.isSome)
		.at(-1);

/**
 * Shown under an answer that has web sources: only a web tool that found something returns
 * them, so one that failed, found nothing, was refused, or hit the turn's limit adds none.
 */
export const assistantWebFindingsWarning =
	"Findings from the public web. Verify them before relying on them.";
